import { Card, Heading, Text } from "@/design-system";
import type { LegalBlock } from "../content/types";
import { LegalRichText } from "./legalRichText";

type LegalTableViewProps = {
  table: Extract<LegalBlock, { type: "table" }>;
};

/**
 * "Tabela" de documento jurídico: cada linha vira um cartão, com a coluna
 * principal de título e as demais como pares termo → descrição.
 *
 * Não é o `DataList`: ali a tabela tem 896px de largura mínima, pensada para
 * dados curtos (valor, data, status). Aqui as células são frases, e a coluna
 * de leitura tem ~700px — a tabela rolaria para o lado até no desktop.
 */
export const LegalTableView = ({ table }: LegalTableViewProps) => {
  const primary =
    table.columns.find(column => column.primary) ?? table.columns[0];
  const details = table.columns.filter(column => column.key !== primary.key);

  return (
    <figure className="flex flex-col gap-3">
      <Text as="figcaption" size="small" weight="semibold">
        {table.caption}
      </Text>
      <ul className="grid gap-3 md:grid-cols-2">
        {table.rows.map(row => (
          <li key={row[primary.key]}>
            <Card padding="compact" className="flex h-full flex-col gap-3">
              <Heading level={3} size={4} className="text-ds-body">
                <LegalRichText text={row[primary.key]} />
              </Heading>
              <dl className="flex flex-col gap-2">
                {details.map(column => (
                  <div key={column.key} className="flex flex-col gap-0.5">
                    <Text
                      as="dt"
                      size="caption"
                      tone="subtle"
                      weight="semibold"
                    >
                      {column.header}
                    </Text>
                    <Text as="dd" size="small" tone="muted">
                      <LegalRichText text={row[column.key] ?? "—"} />
                    </Text>
                  </div>
                ))}
              </dl>
            </Card>
          </li>
        ))}
      </ul>
    </figure>
  );
};
